import { useMemo } from 'react';
import type { IDockviewPanelProps } from 'dockview-react';
import { useStream } from '../../stream/stream';
import { useActiveLogin } from '../../trading/useTrading';
import { useSymbols } from '../../market/useSymbols';
import { useChallenge } from '../../challenge/useChallenge';
import { ClassicTradeTable } from './ClassicTradeTable';
import { exposureOf } from './exposure';
import { ToolboxFrame } from './frames';
import { toggleToolbox } from './classicLayout';

/** A Toolbox window: the strip with the Toolbox's close button, then the window. */
export function ToolboxWindow({
  Component,
  ...props
}: IDockviewPanelProps & { Component: React.FunctionComponent<IDockviewPanelProps> }) {
  const challenge = useChallenge().data !== undefined;
  return (
    <ToolboxFrame onClose={() => toggleToolbox(props.containerApi, challenge)}>
      <Component {...props} />
    </ToolboxFrame>
  );
}

/**
 * The Trade tab, one merged table as MT5 draws it: open positions, sorted; an inline account-summary row
 * (balance, equity, margin, free margin, level); then pending orders — see ClassicTradeTable.
 */
export function ClassicTrade() {
  return (
    <div className="flex h-full flex-col">
      <ClassicTradeTable />
    </div>
  );
}

/** The Exposure tab: the account's net position in each currency or asset, with a bar for its size. */
export function ExposurePanel() {
  const login = useActiveLogin();
  const account = useStream((s) => (login === undefined ? undefined : s.accounts[login]));
  const symbols = useSymbols().data;
  const rows = useMemo(() => {
    if (!account) return [];
    const byName = new Map((symbols ?? []).map((s) => [s.name, s]));
    return exposureOf(Object.values(account.positions), (name) => byName.get(name));
  }, [account, symbols]);
  const largest = Math.max(1, ...rows.map((r) => Math.abs(r.volume)));
  const number = new Intl.NumberFormat(undefined, { maximumFractionDigits: 2 });

  return (
    <section className="h-full overflow-auto bg-panel" aria-label="Exposure">
      <table className="classic-table w-full">
        <thead>
          <tr>
            <th className="text-left">Asset</th>
            <th className="text-right">Volume</th>
            <th className="text-right">Positions</th>
            <th className="w-1/2 text-left">Graph</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && (
            <tr>
              <td colSpan={4} className="text-muted">
                No open positions.
              </td>
            </tr>
          )}
          {rows.map((r) => (
            <tr key={r.asset}>
              <td>{r.asset}</td>
              <td className={`num text-right ${r.volume < 0 ? 'text-down' : ''}`}>
                {number.format(r.volume)}
              </td>
              <td className="num text-right">{r.positions}</td>
              <td>
                <div
                  className={`h-2.5 ${r.volume < 0 ? 'bg-down' : 'bg-accent'}`}
                  style={{ width: `${(Math.abs(r.volume) / largest) * 100}%` }}
                  aria-hidden="true"
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}
